import axios from "axios";
import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { baseUrl } from "../../../config/baseUrl";
import EduPagination from "../../edu-pagination/edu-pagination";
import buildingImg from "../../../assets/images/building_burz.png";
import loadergif from "../../../assets/images/model-loader.gif";

function AddEditCollegeChemicalModal({
  show,
  onClose,
  onSuccess,
  editData,
  colleges,
}) {
  const [chemicalName, setChemicalName] = useState(
    editData?.subcategory_name || ""
  );
  const [chemicalDoc, setChemicalDoc] = useState(null);
  const [chemicalImg, setChemicalImg] = useState(null);
  const [campusList, setCampusList] = useState([]); // [{college_name, campus_name, q}]
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const docInputRef = useRef();
  const imgInputRef = useRef();
  const modalRef = useRef();

  // Update: Add checkbox selection for each campus
  useEffect(() => {
    setChemicalName(editData?.subcategory_name || "");
    setChemicalDoc(null);
    setChemicalImg(null);
    if (docInputRef.current) docInputRef.current.value = "";
    if (imgInputRef.current) imgInputRef.current.value = "";
    if (editData && editData.campus) {
      setCampusList(
        editData.campus.map((c) => ({
          id: c.campus || c.id,
          campus_name: c.campus_name,
          q: c.q,
          selected: c.q !== undefined && c.q !== null && c.q !== "",
        }))
      );
    } else if (colleges) {
      setCampusList(
        colleges.map((c) => ({
          id: c.id,
          campus_name: c.campus_name,
          q: "",
          selected: false,
        }))
      );
    } else {
      setCampusList([]);
    }
  }, [editData, show, colleges]);

  // Close modal on outside click or ESC
  useEffect(() => {
    if (!show) return;
    function handleClick(e) {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        onClose();
      }
    }
    function handleEsc(e) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleEsc);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleEsc);
    };
  }, [show, onClose]);

  // Checkbox and quantity change handlers
  const handleCampusCheckboxChange = (idx, checked) => {
    setCampusList((list) =>
      list.map((c, i) =>
        i === idx ? { ...c, selected: checked, q: checked ? c.q : "" } : c
      )
    );
  };
  const handleCampusQChange = (idx, value) => {
    setCampusList((list) =>
      list.map((c, i) => (i === idx ? { ...c, q: value } : c))
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      // Validate: at least one college must be selected with a quantity
      const selectedWithQty = campusList.filter(
        (c) =>
          c.selected &&
          c.q !== undefined &&
          c.q !== null &&
          String(c.q).trim() !== ""
      );
      if (selectedWithQty.length === 0) {
        setError("Please select at least one college and enter its quantity.");
        setLoading(false);
        return;
      }

      const formData = new FormData();
      formData.append("subcategory_name", chemicalName);
      // Only send selected campuses with q
      const campusPayload = campusList
        .filter((c) => c.selected && c.q && c.q !== "")
        .map((c) => ({ campus: c.id, q: c.q }));
      formData.append("campus", JSON.stringify(campusPayload));
      if (chemicalDoc) formData.append("subcategory_document", chemicalDoc);
      if (chemicalImg) formData.append("subcategory_images", chemicalImg);
      if (editData) {
        await axios.patch(
          `${baseUrl}/update_college_subcategory/${editData.id}/`,
          formData,
          {
            headers: {
              Authorization: "Token " + localStorage.getItem("token"),
              "Content-Type": "multipart/form-data",
            },
          }
        );
      } else {
        await axios.post(`${baseUrl}/create_college_subcategory/`, formData, {
          headers: {
            Authorization: "Token " + localStorage.getItem("token"),
            "Content-Type": "multipart/form-data",
          },
        });
      }
      setChemicalName("");
      setChemicalDoc(null);
      setChemicalImg(null);
      if (docInputRef.current) docInputRef.current.value = "";
      if (imgInputRef.current) imgInputRef.current.value = "";
      if (onSuccess) onSuccess();
      if (onClose) onClose();
    } catch (err) {
      setError("Failed to save chemical.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {show && (
        <div
          className={`modal fade custom-modal addcollege-modal${
            show ? " show d-block" : ""
          }`}
          id="addEditCollegeChemicalModal"
          tabIndex={-1}
          aria-labelledby="addEditCollegeChemicalModalLabel"
          aria-hidden={!show}
          style={{ background: show ? "rgba(0,0,0,0.5)" : undefined }}
          data-bs-backdrop="false"
          data-bs-keyboard="false"
        >
          <div className="modal-dialog modal-fullscreen">
            <div className="modal-content" ref={modalRef}>
              <div className="modal-header justify-content-center">
                <h5 className="modal-title">
                  {editData ? "Edit" : "Add"} College Chemical
                </h5>
                <button
                  type="button"
                  className="modal-close"
                  aria-label="Close"
                  onClick={onClose}
                >
                  <i className="fa fa-times" />
                </button>
              </div>
              <div className="modal-body">
                <div className="d-flex flex-row justify-content-center align-items-center">
                  <div className="col-lg-5 text-center">
                    <div className="addbus-modal-innr">
                      <img
                        src={buildingImg}
                        width="336"
                        height="336"
                        alt="add-edit-college-chemical"
                      />
                      <form onSubmit={handleSubmit}>
                        <div className="row justify-content-center">
                          <div className="frm-bx mb-4 col-lg-8 mb-3 text-start">
                            <label className="form-label">Chemical Name</label>
                            <input
                              type="text"
                              className="form-control"
                              value={chemicalName}
                              onChange={(e) => setChemicalName(e.target.value)}
                              required
                            />
                          </div>
                          <div className="frm-bx mb-4 col-lg-8 mb-3 text-start">
                            <label className="form-label">
                              Chemical Document
                            </label>
                            {editData?.subcategory_document ? (
                              <div className="mb-2">
                                {String(editData.subcategory_document)
                                  .toLowerCase()
                                  .endsWith(".pdf") ? (
                                  <iframe
                                    src={editData.subcategory_document}
                                    width="100%"
                                    height="200"
                                    title="Current Chemical Document"
                                  />
                                ) : (
                                  <a
                                    href={editData.subcategory_document}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn btn-outline-info btn-sm"
                                  >
                                    View Uploaded document
                                  </a>
                                )}
                              </div>
                            ) : null}
                            <input
                              type="file"
                              className="form-control"
                              ref={docInputRef}
                              onChange={(e) =>
                                setChemicalDoc(e.target.files[0])
                              }
                              required={!editData}
                            />
                          </div>
                          <div className="frm-bx mb-4 col-lg-8 mb-3 text-start">
                            <label className="form-label">Chemical Image</label>
                            {editData?.subcategory_images ? (
                              <div className="mb-2">
                                <img
                                  src={editData.subcategory_images}
                                  alt="Current Chemical"
                                  style={{
                                    maxWidth: "100%",
                                    height: "auto",
                                    maxHeight: 200,
                                  }}
                                />
                              </div>
                            ) : null}
                            <input
                              type="file"
                              className="form-control"
                              ref={imgInputRef}
                              onChange={(e) =>
                                setChemicalImg(e.target.files[0])
                              }
                              required={!editData}
                            />
                          </div>
                          <div className="frm-bx mb-4 col-lg-8 mb-3 text-start">
                            <label className="form-label">
                              College - Chemical Quantity
                            </label>
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "12px",
                                width: "100%",
                              }}
                            >
                              {campusList.map((c, idx) => (
                                <div
                                  key={c.id}
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "16px",
                                    width: "100%",
                                    marginBottom: 8,
                                  }}
                                >
                                  <input
                                    type="checkbox"
                                    checked={!!c.selected}
                                    onChange={(e) =>
                                      handleCampusCheckboxChange(
                                        idx,
                                        e.target.checked
                                      )
                                    }
                                    style={{ marginRight: 4 }}
                                  />
                                  <div
                                    style={{ minWidth: 160, fontWeight: 500 }}
                                  >
                                    {c.campus_name}
                                  </div>
                                  <input
                                    type="number"
                                    className="form-control"
                                    style={{
                                      width: 100,
                                      marginLeft: 0,
                                      marginRight: 0,
                                    }}
                                    value={c.q}
                                    min={0}
                                    onChange={(e) =>
                                      handleCampusQChange(idx, e.target.value)
                                    }
                                    disabled={!c.selected}
                                    required={c.selected}
                                    placeholder="Qty"
                                  />
                                </div>
                              ))}
                            </div>
                          </div>
                          <div className="frm-bx mb-4 col-lg-8 mb-3">
                            <button
                              className="thm-btn"
                              type="submit"
                              disabled={loading}
                            >
                              {loading
                                ? editData
                                  ? "Updating..."
                                  : "Adding..."
                                : editData
                                ? "Update Chemical"
                                : "Add Chemical"}
                            </button>
                          </div>
                          {error && (
                            <div className="col-12 text-danger">{error}</div>
                          )}
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function DeleteCollegeChemicalModal({ show, onClose, onConfirm }) {
  const modalRef = useRef();
  useEffect(() => {
    if (!show) return;
    function handleClick(e) {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        onClose();
      }
    }
    function handleEsc(e) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleEsc);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleEsc);
    };
  }, [show, onClose]);
  return (
    <>
      {show && (
        <div
          className={`modal fade${show ? " show d-block" : ""}`}
          id="deleteCollegeChemicalModal"
          tabIndex={-1}
          aria-labelledby="deleteCollegeChemicalModalLabel"
          aria-hidden={!show}
          style={{ background: show ? "rgba(0,0,0,0.5)" : undefined }}
          data-bs-backdrop="false"
          data-bs-keyboard="false"
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content" ref={modalRef}>
              <div className="modal-body">
                <div className="modal-trash">
                  <img src={loadergif} alt="delete" className="w-100" />
                  <h4>Are you sure you want to delete this chemical?</h4>
                  <div className="modal-trash-btn">
                    <button className="btn btn-outline-info" onClick={onClose}>
                      Cancel
                    </button>
                    <button
                      className="btn btn-outline-danger"
                      onClick={onConfirm}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export const EduChemicalSDSList = () => {
  const [chemicals, setChemicals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [pageNumber, setPageNumber] = useState(1);
  const [pageLimit, setPageLimit] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editChemical, setEditChemical] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteChemicalId, setDeleteChemicalId] = useState(null);
  const [colleges, setColleges] = useState([]);
  const [refresh, setRefresh] = useState(false);

  // New state for the three sections
  const [sdsList, setSdsList] = useState([]);
  const [sdsLoading, setSdsLoading] = useState(true);
  const [sdsError, setSdsError] = useState("");

  const [docList, setDocList] = useState([]);
  const [docLoading, setDocLoading] = useState(true);
  const [docError, setDocError] = useState("");

  const [policyList, setPolicyList] = useState([]);
  const [policyLoading, setPolicyLoading] = useState(true);
  const [policyError, setPolicyError] = useState("");

  // Fetch SDS (chemicals)
  useEffect(() => {
    setSdsLoading(true);
    setSdsError("");
    axios
      .get(`${baseUrl}/get_college_subcategory/?per_page=100&page=1`, {
        headers: { Authorization: "Token " + localStorage.getItem("token") },
      })
      .then((res) => {
        setSdsList(res.data?.data || []);
        setSdsLoading(false);
      })
      .catch(() => {
        setSdsError("Failed to fetch chemicals.");
        setSdsList([]);
        setSdsLoading(false);
      });
  }, [refresh]);

  // Fetch Health Safety Documents
  useEffect(() => {
    setDocLoading(true);
    setDocError("");
    axios
      .get(`${baseUrl}/get_college_health_document/`, {
        headers: { Authorization: "Token " + localStorage.getItem("token") },
      })
      .then((res) => {
        setDocList(res.data?.data || []);
        setDocLoading(false);
      })
      .catch(() => {
        setDocError("Failed to fetch health safety documents.");
        setDocList([]);
        setDocLoading(false);
      });
  }, [refresh]);

  // Fetch Health Safety Policies
  useEffect(() => {
    setPolicyLoading(true);
    setPolicyError("");
    axios
      .get(`${baseUrl}/get_college_health_policy/?per_page=100&page=1`, {
        headers: { Authorization: "Token " + localStorage.getItem("token") },
      })
      .then((res) => {
        setPolicyList(res.data?.data || []);
        setPolicyLoading(false);
      })
      .catch(() => {
        setPolicyError("Failed to fetch health safety policies.");
        setPolicyList([]);
        setPolicyLoading(false);
      });
  }, [refresh]);

  useEffect(() => {
    async function fetchChemicals() {
      setLoading(true);
      setError("");
      try {
        const res = await axios.get(
          `${baseUrl}/get_college_subcategory/?per_page=${pageLimit}&page=${pageNumber}`,
          {
            headers: {
              Authorization: "Token " + localStorage.getItem("token"),
            },
          }
        );
        if (res.data && res.data.data) {
          setChemicals(res.data.data);
          setTotalCount(res.data.count || 0);
        } else {
          setChemicals([]);
          setTotalCount(0);
        }
      } catch (err) {
        setError("Failed to fetch chemicals.");
        setChemicals([]);
        setTotalCount(0);
      } finally {
        setLoading(false);
      }
    }
    fetchChemicals();
  }, [pageNumber, pageLimit, refresh]);

  useEffect(() => {
    async function fetchColleges() {
      try {
        const res = await axios.get(`${baseUrl}/get_campus/`, {
          headers: { Authorization: "Token " + localStorage.getItem("token") },
        });
        if (res.data && res.data.data) {
          setColleges(res.data.data);
        }
      } catch (err) {
        setColleges([]);
      }
    }
    fetchColleges();
  }, []);

  const handleDelete = async () => {
    if (!deleteChemicalId) return;
    try {
      await axios.delete(
        `${baseUrl}/delete_college_subcategory/${deleteChemicalId}/`,
        {
          headers: { Authorization: "Token " + localStorage.getItem("token") },
        }
      );
      setShowDeleteModal(false);
      setDeleteChemicalId(null);
      setRefresh((r) => !r);
    } catch (err) {
      setShowDeleteModal(false);
      setDeleteChemicalId(null);
    }
  };

  return (
    <section className="main-sec">
      <div className="row">
        <div className="col-lg-12">
          <div className="dashboard-title">
            <h4 className="dash-head">
              <i className="fa fa-chart-bar me-2" />
              College Chemical List
            </h4>
          </div>
          <div className="custom-bredcump">
            <nav aria-label="breadcrumb">
              <ol className="breadcrumb">
                <li className="breadcrumb-item">
                  <Link to="/education">Dashboard</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  College Chemical List
                </li>
              </ol>
            </nav>
          </div>
        </div>
        {/* Existing table and modals below */}
        <div className="col-lg-12">
          <div className="cards bus-list">
            <div className="d-flex flex-row justify-content-between align-items-center mb-3">
              <div></div>
              <button className="blu-btn" onClick={() => setShowAddModal(true)}>
                <i className="fa fa-plus me-2" /> Add College Chemical
              </button>
            </div>
            {showAddModal && (
              <AddEditCollegeChemicalModal
                show={showAddModal}
                onClose={() => setShowAddModal(false)}
                onSuccess={() => {
                  setShowAddModal(false);
                  setRefresh((r) => !r);
                }}
                colleges={colleges}
              />
            )}
            {showEditModal && (
              <AddEditCollegeChemicalModal
                show={showEditModal}
                onClose={() => setShowEditModal(false)}
                onSuccess={() => {
                  setShowEditModal(false);
                  setRefresh((r) => !r);
                }}
                editData={editChemical}
                colleges={colleges}
              />
            )}
            {showDeleteModal && (
              <DeleteCollegeChemicalModal
                show={showDeleteModal}
                onClose={() => setShowDeleteModal(false)}
                onConfirm={handleDelete}
              />
            )}
            <div className="table table-responsive custom-table">
              <table className="table table-borderless">
                <thead>
                  <tr>
                    <th>Sr No.</th>
                    <th>Image</th>
                    <th>Chemical Name</th>
                    <th>College - Chemical Quantity</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="5">Loading...</td>
                    </tr>
                  ) : error ? (
                    <tr>
                      <td colSpan="5" className="text-danger">
                        {error}
                      </td>
                    </tr>
                  ) : chemicals.length > 0 ? (
                    chemicals.map((chem, i) => (
                      <tr key={chem.id}>
                        <td>{i + 1 + pageLimit * (pageNumber - 1)}</td>
                        <td>
                          <img
                            src={chem.subcategory_images}
                            alt="chemical_images"
                            style={{ width: "50px", height: "50px" }}
                          />
                        </td>
                        <td>{chem.subcategory_name}</td>
                        <td>
                          {chem.campus?.map((c, idx) => (
                            <div key={idx}>
                              {(c.college_name || c.campus_name) +
                                " - " +
                                c.q +
                                " L"}
                            </div>
                          ))}
                        </td>
                        <td>
                          <button
                            className="btn btn-outline-warning btn-sm me-2"
                            onClick={() => {
                              setEditChemical(chem);
                              setShowEditModal(true);
                            }}
                            title="Update"
                          >
                            <i className="fa fa-edit" />
                          </button>
                          <button
                            className="btn btn-outline-danger btn-sm"
                            onClick={() => {
                              setDeleteChemicalId(chem.id);
                              setShowDeleteModal(true);
                            }}
                            title="Delete"
                          >
                            <i className="fa fa-trash" />
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5">No chemicals found.</td>
                    </tr>
                  )}
                </tbody>
              </table>
              <EduPagination
                pageNumber={pageNumber}
                setPageNumber={setPageNumber}
                pageLimit={pageLimit}
                setPageLimit={setPageLimit}
                totalCount={totalCount}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
